/**
 * The browser's one door to the server. Every failure becomes an ApiFailure
 * carrying the message the server wrote for the person, or our own copy when
 * the server could not be reached at all. An answer that the person is no
 * longer signed in is also told to the session (onSessionEnded), so the app
 * goes back to the sign-in screen from wherever it was.
 */
import type { TodoItem } from '../lib/admin/todo.ts';
import { copy } from './copy/en-CA.ts';

export type { TodoItem } from '../lib/admin/todo.ts';

export type Role = 'viewer' | 'physician' | 'admin';

export interface User {
  id: string;
  email: string;
  displayName: string;
  isAdmin: boolean;
  physicianId: string | null;
  physicianName: string | null;
  role: Role;
  active: boolean;
  mustChangePassword: boolean;
  lastSignInAt: string | null;
}

export interface Physician {
  id: string;
  name: string;
  initials: string;
  colour: string;
  startDate: string | null;
  endDate: string | null;
  active: boolean;
  sortOrder: number;
}

export type WeekKind = 'regular' | 'long-weekend' | 'christmas' | 'new-year';

export interface WeekView {
  weekStart: string;
  onCallStart: string;
  onCallEnd: string;
  days: number;
  kind: WeekKind;
  longWeekendHolidays: string[];
  coveredMonday: { date: string; holiday: string } | null;
  skippedMonday: { date: string; holiday: string } | null;
  specialOverride: 'christmas' | 'new-year' | 'regular' | null;
  physicianId: string | null;
  conflicts: string[];
  manual: boolean;
}

export interface ScheduleSummary {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  status: 'draft' | 'saved';
  civicHoliday: boolean;
  createdAt: string;
  savedAt: string | null;
  /** The day it was released, in Toronto: what the pages show. */
  savedOn: string | null;
  weekCount: number;
}

export interface DistributionRow {
  physicianId: string;
  name: string;
  weeks: number;
  longWeekends: number;
  specialWeeks: number;
  leaveWeeks: number;
  weeksBeforeStart: number;
  weeksAfterEnd: number;
  twelveMonthLongWeekends: number;
  /** Admins only. */
  lastSpecialWeek?: string | null;
}

export interface PersonalSummary {
  physicianId: string;
  weekStarts: string[];
  /** The first week starting after today, across every saved schedule. */
  nextWeek: WeekView | null;
  nextWeekSchedule: { id: string; name: string } | null;
  weeks: number;
  longWeekends: number;
  specialWeeks: number;
  twelveMonthLongWeekends: number;
}

export interface ScheduleDetail {
  schedule: ScheduleSummary & { unassignedWeeks: number; conflictWeeks: number; longWeekendSpreadOk: boolean };
  physicians: Physician[];
  weeks: WeekView[];
  distribution: DistributionRow[];
  me: PersonalSummary | null;
  today: string;
  /** Who is on call today, from whichever saved schedule holds today. */
  onCallNow: OnCallNow | null;
}

export interface OnCallNow {
  week: WeekView;
  scheduleId: string;
  scheduleName: string;
  physician: { id: string; name: string; colour: string } | null;
}

/**
 * One line of "Your on-call weeks changed": the net change an admin made to
 * one week of a saved schedule, as it concerns the signed-in physician.
 */
export interface WeekChange {
  scheduleId: string;
  weekStart: string;
  onCallStart: string;
  onCallEnd: string;
  days: number;
  /** True when the week is now theirs; false when it no longer is. */
  gained: boolean;
  /** Null when the week had nobody before, or has nobody now. */
  from: { id: string; name: string } | null;
  to: { id: string; name: string } | null;
  /** The name of the admin who made the change; null if unknown. */
  changedBy: string | null;
  changedAt: string;
  /** The day it was made, in the group's clock. */
  changedOn: string;
}

export interface RequestPeriod {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  dueDate: string;
  open: boolean;
  closedAt: string | null;
  createdAt: string;
  pendingCount: number;
  requestCount: number;
  physiciansSubmitted: number;
}

export type RequestKind = 'vacation' | 'preferred' | 'avoid';
export type RequestStatus = 'pending' | 'approved' | 'declined';

/** A week an admin entered for the signed-in physician, shown read-only on the request screen. */
export interface AdminEntry {
  weekStart: string;
  kind: AvailabilityKind;
}

/** Any physician's admin entry, for the admin's review list. */
export interface PhysicianEntry extends AdminEntry {
  physicianId: string;
}

/** The request period a new one was refused for overlapping (error code requestPeriodOverlaps). */
export interface PeriodRef {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
}

export interface RequestView {
  id: string;
  periodId: string;
  physicianId: string;
  physicianName: string;
  weekStart: string;
  kind: RequestKind;
  status: RequestStatus;
  submittedAt: string;
  decidedAt: string | null;
}

/** A week as the pure module builds it, before anyone is assigned. */
export interface PlainWeek {
  weekStart: string;
  onCallStart: string;
  onCallEnd: string;
  days: number;
  kind: WeekKind;
  longWeekendHolidays: string[];
  coveredMonday: { date: string; holiday: string } | null;
  skippedMonday: { date: string; holiday: string } | null;
  overridden: boolean;
}

export type AvailabilityKind = 'vacation' | 'loa' | 'preferred' | 'avoid';

export interface AvailabilityCell {
  physicianId: string;
  weekStart: string;
  kind: AvailabilityKind;
  source: 'admin' | 'request';
  status?: RequestStatus;
  requestId?: string;
}

export interface AuditEntry {
  id: number;
  at: string;
  userName: string | null;
  userEmail: string | null;
  action: string;
  entity: string;
  entityId: string | null;
  details: Record<string, unknown>;
}

export interface RangeRow {
  physicianId: string;
  name: string;
  weeks: number;
  longWeekends: number;
  specialWeeks: number;
  availableWeeks: number;
  leaveWeeks: number;
  expectedWeeks: number;
  perAvailableWeek: number | null;
}

export interface RangeDistribution {
  from: string;
  to: string;
  scheduledWeeks: number;
  rows: RangeRow[];
}

export interface HolidayCheckStatus {
  checkedAt: string | null;
  lastSuccessAt: string | null;
  stale: boolean;
  failure: 'timeout' | 'unreachable' | 'unexpected-answer' | null;
  years: {
    year: number;
    compared: number;
    disagreements: { name: string; what: 'date' | 'observed'; ours: string; theirs: string }[];
    unknownToUs: { name: string; date: string }[];
    unknownToApi: { name: string; date: string }[];
    ok: boolean;
  }[];
}

export type CalendarFeed = 'mine' | 'full';

export interface CalendarLinks {
  feeds: { feed: CalendarFeed; path: string | null }[];
}

export interface RequestProblem {
  kind: string;
  weekStarts: string[];
}

export class ApiFailure extends Error {
  readonly status: number;
  readonly code: string;
  /** Extra, structured detail some errors carry (the request limits, for one). */
  readonly problems: RequestProblem[];
  /** The period a new request period would overlap, so the screen can name it. */
  readonly period: PeriodRef | null;

  constructor(status: number, code: string, message: string, problems: RequestProblem[] = [], period: PeriodRef | null = null) {
    super(message);
    this.status = status;
    this.code = code;
    this.problems = problems;
    this.period = period;
  }
}

/**
 * True for the server's answer that a request needs a signed-in person: the
 * session has ended (signed out on another device, password reset, account
 * deactivated, or expired). Not a wrong password at sign-in ("signInFailed"),
 * and not "signInAgain" at first sign-in, which that page explains itself.
 */
export function isSessionEnded(err: unknown): boolean {
  return err instanceof ApiFailure && err.status === 401 && err.code === 'signInRequired';
}

const sessionEndedListeners = new Set<() => void>();

/**
 * Calls `listener` whenever any request finds the session has ended. The
 * session provider uses it to clear the signed-in person, in this one place,
 * rather than each page showing "Please sign in to continue." under a header
 * that still names them. Returns the function that stops listening.
 */
export function onSessionEnded(listener: () => void): () => void {
  sessionEndedListeners.add(listener);
  return () => {
    sessionEndedListeners.delete(listener);
  };
}

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      method,
      headers: body === undefined ? {} : { 'content-type': 'application/json' },
      body: body === undefined ? null : JSON.stringify(body),
      credentials: 'same-origin',
    });
  } catch {
    throw new ApiFailure(0, 'network', copy.errors.network);
  }
  if (res.status === 204) return null as T;
  const text = await res.text();
  let json: unknown = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = null;
  }
  if (!res.ok) {
    const err = (json as { error?: { code?: string; message?: string; problems?: RequestProblem[]; period?: PeriodRef } } | null)?.error;
    const failure = new ApiFailure(res.status, err?.code ?? 'unexpected', err?.message ?? copy.errors.unexpected, err?.problems ?? [], err?.period ?? null);
    if (isSessionEnded(failure)) for (const listener of [...sessionEndedListeners]) listener();
    throw failure;
  }
  return json as T;
}

export const api = {
  me: () => call<{ user: User }>('GET', '/api/me'),
  signIn: (email: string, password: string) => call<{ user: User }>('POST', '/api/session', { email, password }),
  signOut: () => call<null>('DELETE', '/api/session'),
  /** `currentPassword` is null only at first sign-in, where the server allows leaving it out for a while. */
  changePassword: (currentPassword: string | null, newPassword: string) =>
    call<null>('POST', '/api/me/password', currentPassword === null ? { newPassword } : { currentPassword, newPassword }),
  /** "Forgot your password?": answers the same whether or not an account uses the address. */
  requestPasswordReset: (email: string) => call<null>('POST', '/api/password-reset', { email }),
  /** Whether an emailed link still works, and for which account; does not use it up. */
  checkResetLink: (token: string) => call<{ email: string }>('POST', '/api/password-reset/check', { token }),
  completePasswordReset: (token: string, newPassword: string) => call<{ email: string }>('POST', '/api/password-reset/complete', { token, newPassword }),

  /** `asOf` goes back with "Got it", so a change made while the page was open is still told next time. */
  weekChanges: () => call<{ changes: WeekChange[]; asOf: string }>('GET', '/api/me/week-changes'),
  weekChangesSeen: (through: string) => call<null>('POST', '/api/me/week-changes/seen', { through }),

  calendarLinks: () => call<CalendarLinks>('GET', '/api/me/calendar'),
  makeCalendarLink: (feed: CalendarFeed) => call<CalendarLinks>('POST', `/api/me/calendar/${feed}`, {}),
  turnOffCalendarLink: (feed: CalendarFeed) => call<CalendarLinks>('DELETE', `/api/me/calendar/${feed}`),

  physicians: () => call<{ physicians: Physician[] }>('GET', '/api/physicians'),
  settings: () => call<{ civicHoliday: boolean }>('GET', '/api/settings'),
  schedules: () => call<{ schedules: ScheduleSummary[]; currentId: string | null; suggestedStart: string | null; today: string }>('GET', '/api/schedules'),
  currentSchedule: () => call<{ schedule: ScheduleDetail | null; today: string }>('GET', '/api/schedules/current'),
  schedule: (id: string) => call<{ schedule: ScheduleDetail; today: string }>('GET', `/api/schedules/${id}`),
  csvUrl: (id: string) => `/api/schedules/${id}/export.csv`,
  exportUrl: () => '/api/admin/export.json',
  /** `answered`: for a physician, the day (Toronto's) they last answered each period, by period id; an empty answer ("Nothing needed this time") counts. */
  requestPeriods: () =>
    call<{
      periods: RequestPeriod[];
      mine: Record<string, number>;
      mineByStatus: Record<string, Record<RequestStatus, number>>;
      answered: Record<string, string>;
      today: string;
    }>('GET', '/api/request-periods'),
  /** `submittedOn`: the day the signed-in physician last answered this period, or null. */
  requestPeriod: (id: string) =>
    call<{ period: RequestPeriod; weeks: PlainWeek[]; requests: RequestView[]; entries: AdminEntry[]; submittedOn: string | null; today: string }>('GET', `/api/request-periods/${id}`),
  /** An empty list is an answer too: "Nothing needed this time". */
  submitRequests: (id: string, requests: { weekStart: string; kind: RequestKind }[]) =>
    call<{ requests: RequestView[]; warnings: { kind: string; weekStarts: string[] }[]; submittedOn: string }>('PUT', `/api/request-periods/${id}/requests`, { requests }),

  admin: {
    listUsers: () => call<{ users: User[] }>('GET', '/api/admin/users'),
    createUser: (input: { email: string; displayName: string; isAdmin: boolean; physicianId?: string | null }) =>
      call<{ user: User; temporaryPassword: string }>('POST', '/api/admin/users', input),
    updateUser: (id: string, patch: { email?: string; isAdmin?: boolean; active?: boolean; displayName?: string; physicianId?: string | null }) =>
      call<{ user: User }>('PATCH', `/api/admin/users/${id}`, patch),
    resetPassword: (id: string) => call<{ temporaryPassword: string }>('POST', `/api/admin/users/${id}/password-reset`, {}),

    listPhysicians: () => call<{ physicians: Physician[] }>('GET', '/api/admin/physicians'),
    createPhysician: (input: { name: string; initials?: string; colour?: string; startDate?: string | null; endDate?: string | null }) =>
      call<{ physician: Physician }>('POST', '/api/admin/physicians', input),
    updatePhysician: (
      id: string,
      patch: { name?: string; initials?: string; colour?: string; startDate?: string | null; endDate?: string | null; active?: boolean; sortOrder?: number },
    ) => call<{ physician: Physician }>('PATCH', `/api/admin/physicians/${id}`, patch),

    setSettings: (input: { civicHoliday: boolean }) => call<{ civicHoliday: boolean }>('PUT', '/api/admin/settings', input),
    /** `overrides`: only the Christmas and New Year weeks the admin changed from the rule's pick. */
    buildSchedule: (input: { start: string; months?: number; end?: string; name?: string; overrides?: Record<string, 'christmas' | 'new-year' | 'regular'> }) =>
      call<{ schedule: ScheduleDetail }>('POST', '/api/admin/schedules', input),
    regenerate: (id: string) => call<{ schedule: ScheduleDetail }>('POST', `/api/admin/schedules/${id}/regenerate`, {}),
    saveSchedule: (id: string, name: string) => call<{ schedule: ScheduleDetail }>('POST', `/api/admin/schedules/${id}/save`, { name }),
    deleteSchedule: (id: string) => call<null>('DELETE', `/api/admin/schedules/${id}`),
    reassign: (id: string, weekStart: string, physicianId: string | null) =>
      call<{ schedule: ScheduleDetail; conflicts: Record<string, string[]> }>('PATCH', `/api/admin/schedules/${id}/weeks/${weekStart}`, { physicianId }),
    swap: (id: string, a: string, b: string) => call<{ schedule: ScheduleDetail; conflicts: Record<string, string[]> }>('POST', `/api/admin/schedules/${id}/swap`, { a, b }),
    setWeekKind: (id: string, weekStart: string, specialOverride: 'christmas' | 'new-year' | 'regular' | null) =>
      call<{ schedule: ScheduleDetail }>('PATCH', `/api/admin/schedules/${id}/weeks/${weekStart}/kind`, { specialOverride }),

    createRequestPeriod: (input: { start: string; end: string; dueDate: string; name?: string }) => call<{ period: RequestPeriod }>('POST', '/api/admin/request-periods', input),
    updateRequestPeriod: (id: string, patch: { name?: string; dueDate?: string; closed?: boolean }) =>
      call<{ period: RequestPeriod }>('PATCH', `/api/admin/request-periods/${id}`, patch),
    deleteRequestPeriod: (id: string) => call<null>('DELETE', `/api/admin/request-periods/${id}`),
    /** Every physician's requests in a period, for the admin's review list. */
    periodRequests: (id: string) => call<{ requests: RequestView[]; entries: PhysicianEntry[] }>('GET', `/api/admin/request-periods/${id}/requests`),
    removeRequest: (id: string) => call<null>('DELETE', `/api/admin/requests/${id}`),
    decideRequest: (id: string, status: RequestStatus) => call<{ requests: RequestView[] }>('POST', `/api/admin/requests/${id}/decision`, { status }),
    approveAll: (periodId: string) => call<{ requests: RequestView[]; approved: number }>('POST', `/api/admin/request-periods/${periodId}/approve-all`, {}),
    availability: (start: string, end: string) =>
      call<{ weeks: PlainWeek[]; physicians: Physician[]; cells: AvailabilityCell[] }>('GET', `/api/admin/availability?start=${start}&end=${end}`),
    setAvailability: (physicianId: string, weekStart: string, kind: AvailabilityKind | null) => call<null>('PUT', '/api/admin/availability', { physicianId, weekStart, kind }),
    rangeDistribution: (start: string, end: string) => call<RangeDistribution>('GET', `/api/admin/distribution?start=${start}&end=${end}`),
    audit: (limit = 200) => call<{ entries: AuditEntry[] }>('GET', `/api/admin/audit?limit=${String(limit)}`),
    holidayCheck: () => call<HolidayCheckStatus>('GET', '/api/admin/holiday-check'),
    /** What is waiting for an admin (the To do page), and so the count on the Admin tab. */
    todo: () => call<{ items: TodoItem[]; today: string }>('GET', '/api/admin/todo'),
  },
};
